import React, { useMemo, useState } from 'react';
import type { Account } from '@axiom/validation';
import {
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Dropdown,
  Button,
  Banner,
  Gutter,
  AsyncConnector,
} from '@axiom/ui';

import type { ReadAccountsPayload } from '../../api/account-api';
import { AccountApi } from '../../api/account-api';
import { EnvUtil } from '../../utils/env-util';

import { ImpersonationAccountPickerModalFormSchema } from './impersonation-account-picker-modal-form-schema';

export type ImpersonationAccountPickerModalProps = {
  selectAccount: (prop: Account) => void;
  onClose: () => void;
};

export const ImpersonationAccountPickerModal = ({
  selectAccount,
  onClose,
}: ImpersonationAccountPickerModalProps) => {
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const dataSource = useMemo(
    () =>
      new AsyncConnector<ReadAccountsPayload>({
        getData: ({ search, page }) => {
          return AccountApi.readAccounts({
            search,
            page,
          });
        },
        parseData: results => {
          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: results.data,
          };
        },
      }),
    []
  );

  const handleOnSubmit = () => {
    selectAccount(selectedAccount as Account);
  };

  const handleBannerLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();

    window.open(EnvUtil.envoyUrl, '_blank');
    onClose();
  };

  return (
    <Form
      name="IMPERSONATION_ACCOUNT_PICKER_FORM"
      schema={ImpersonationAccountPickerModalFormSchema}
      initialValues={{
        ACCOUNT_ID: '',
      }}
      onSubmit={handleOnSubmit}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="IMPERSONATION_ACCOUNT_PICKER_MODAL" size="confirm">
            <ModalHeader
              name="MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Provide account details
            </ModalHeader>
            <ModalSection disableScrolling>
              {selectedAccount?.contacts.length === 0 ? (
                <Gutter bottom="24px">
                  <Banner name="NO_CONTACTS_BANNER" type="error" impact="high">
                    No Contacts exist for the selected account. Please either{' '}
                    <a
                      data-test="BANNER_LINK"
                      href={EnvUtil.envoyUrl}
                      onClick={handleBannerLinkClick}
                    >
                      add a contact to this account in AHQ
                    </a>{' '}
                    or select another Account and try again.
                  </Banner>
                </Gutter>
              ) : null}
              <Dropdown
                label="Account"
                description="Search for the account you would like to associate with this engagement."
                name="ACCOUNT_ID"
                options={dataSource}
                displayKey="calculatedName"
                valueKey="id"
                onChange={(accountId, e, account) => {
                  const foundAccount = account as Account;
                  setSelectedAccount(foundAccount);
                }}
              />
            </ModalSection>
            <ModalFooter>
              <Button
                name="PICK_ACCOUNT_CANCEL_BUTTON"
                variation="outline"
                type="button"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                name="PICK_ACCOUNT_NEXT_BUTTON"
                type="submit"
                onClick={fireSubmit}
              >
                Next
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
